import { createContext, useContext, useState, useCallback, useMemo, ReactNode, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { AppMode } from '@/types/appMode';
import { allNavigableRoutes } from '@/config/navigationItems';
import { useRenderDebug } from '@/hooks/useRenderDebug';

interface AppModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
}

const APP_MODE_KEY = 'sustaina-app-mode';
const DEFAULT_MODE: AppMode = 'accounting';
const VALID_MODES: AppMode[] = ['accounting', 'projects', 'sustainability'];

// Route prefixes that should auto-switch the mode
// These are the main entry points for each mode
const MODE_ROUTE_PREFIXES: Record<string, AppMode> = {
  'accounting': 'accounting',
  'projects': 'projects',
  // Impact moved from /accounting/impact (and /sustainability) to /impact;
  // it is the sustainability mode's home.
  'impact': 'sustainability',
  'sustainability': 'sustainability',
};

// Derive modeRoutes from navigation items (single source of truth in navigationItems.ts)
function buildModeRoutes(): Record<AppMode, string[]> {
  const routes: Record<AppMode, string[]> = {
    accounting: [],
    projects: [],
    sustainability: [],
  };

  for (const item of allNavigableRoutes) {
    for (const mode of item.modes) {
      if (!routes[mode].includes(item.href)) {
        routes[mode].push(item.href);
      }
    }
  }

  return routes;
}

const modeRoutes = buildModeRoutes();

// Compile all routes that are mode-specific (union of all mode route lists)
// Routes NOT in this set are considered universal and accessible in all modes
const allModeSpecificRoutes = new Set(
  Object.values(modeRoutes).flat()
);

const AppModeContext = createContext<AppModeContextType | undefined>(undefined);

function getStoredMode(): AppMode {
  try {
    const stored = localStorage.getItem(APP_MODE_KEY);
    if (stored && VALID_MODES.includes(stored as AppMode)) {
      return stored as AppMode;
    }
    return DEFAULT_MODE;
  } catch {
    return DEFAULT_MODE;
  }
}

// Check if a route is valid for a given mode
function isRouteValidForMode(pathname: string, mode: AppMode): boolean {
  // Extract the route segment after /org/:orgId/
  const match = pathname.match(/^\/org\/[^/]+\/([^/]+)/);
  if (!match) return true; // Non-org routes are always valid

  const routeSegment = match[1];

  // If route is not mode-specific at all, allow it (e.g., inbox, assessments)
  if (!allModeSpecificRoutes.has(routeSegment)) return true;

  // Route is mode-specific - check if it's valid for current mode
  return modeRoutes[mode].includes(routeSegment);
}

// Detect mode from route prefix
function detectModeFromRoute(pathname: string): AppMode | null {
  const match = pathname.match(/^\/org\/[^/]+\/([^/]+)/);
  if (!match) return null;

  const routeSegment = match[1];
  return MODE_ROUTE_PREFIXES[routeSegment] || null;
}

export function AppModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<AppMode>(getStoredMode);
  const location = useLocation();
  const navigate = useNavigate();
  // Track if mode change was triggered by user (vs route detection)
  const userInitiatedModeChange = useRef(false);

  const setMode = useCallback((newMode: AppMode) => {
    userInitiatedModeChange.current = true;
    setModeState(newMode);
    try {
      localStorage.setItem(APP_MODE_KEY, newMode);
    } catch (e) {
      console.error('Failed to save app mode to localStorage:', e);
    }
  }, []);

  // Auto-detect and switch mode based on route
  useEffect(() => {
    // Skip if the mode change was user-initiated (clicking a tab)
    if (userInitiatedModeChange.current) {
      userInitiatedModeChange.current = false;
      return;
    }

    const detectedMode = detectModeFromRoute(location.pathname);
    if (detectedMode && detectedMode !== mode) {
      // Route indicates a different mode - switch to it
      setModeState(detectedMode);
      try {
        localStorage.setItem(APP_MODE_KEY, detectedMode);
      } catch (e) {
        console.error('Failed to save app mode to localStorage:', e);
      }
    }
  }, [location.pathname, mode]);

  // When mode changes via user action, check if current route is valid for new mode
  useEffect(() => {
    // Skip validation for mode entry point routes - the mode detection effect will handle these
    const detectedMode = detectModeFromRoute(location.pathname);
    if (detectedMode) {
      return; // This is a mode entry route, let mode detection handle it
    }

    if (!isRouteValidForMode(location.pathname, mode)) {
      // Extract org ID and redirect to dashboard
      const match = location.pathname.match(/^\/org\/([^/]+)/);
      if (match) {
        navigate(`/org/${match[1]}/dashboard`, { replace: true });
      }
    }
  }, [mode, location.pathname, navigate]);

  // DEBUG: Track what's causing re-renders
  useRenderDebug('AppModeProvider', { mode, setMode, locationPathname: location.pathname, navigate });

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
}

export function useAppMode() {
  const context = useContext(AppModeContext);
  if (context === undefined) {
    throw new Error('useAppMode must be used within an AppModeProvider');
  }
  return context;
}
